/**
 * Expensify Integration Service
 * Handles travel expense automation and synchronization with Expensify
 */

import { supabase } from '@/integrations/supabase/client';

export interface ExpensifyConfig {
  partner_user_id: string;
  partner_user_secret: string;
  policy_id?: string;
  webhook_url?: string;
  auto_import: boolean;
  auto_approve_threshold: number;
}

export interface ExpensifyReport {
  reportID: string;
  reportName: string;
  userEmail: string;
  total: number;
  created: string;
  status: string;
  expenses: ExpensifyExpense[];
}

export interface ExpensifyExpense {
  expenseID: string;
  amount: number;
  merchant: string;
  category: string;
  tag?: string;
  comment?: string;
  created: string;
  receipt?: {
    url: string;
    filename: string;
  };
}

export interface ExpenseSyncResult {
  success: boolean;
  expense_id?: string;
  expensify_expense_id: string;
  amount: number;
  error?: string;
}

class ExpensifyService {
  private baseUrl = 'https://integrations.expensify.com/Integration-Server/ExpensifyIntegrations';
  private config: ExpensifyConfig | null = null;

  /**
   * Initialize service with configuration
   */
  async initialize(organizationId: string): Promise<void> {
    try {
      // Get Expensify configuration from database
      const { data: config, error } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', organizationId)
        .eq('integration_type', 'expensify')
        .eq('is_active', true)
        .single();

      if (error || !config) {
        throw new Error('Expensify configuration not found');
      }

      this.config = config.config_data as ExpensifyConfig;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Authenticate with Expensify API
   */
  private async makeExpensifyRequest(requestData: any): Promise<any> {
    if (!this.config) {
      throw new Error('Expensify service not initialized');
    }

    const requestBody = {
      requestJobDescription: JSON.stringify(requestData),
      partnerUserID: this.config.partner_user_id,
      partnerUserSecret: this.config.partner_user_secret,
    };

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Expensify API error: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Import expenses from Expensify report
   */
  async importExpensesFromReport(
    reportId: string,
    projectId: string
  ): Promise<ExpenseSyncResult[]> {
    try {
      // Get report details from Expensify
      const reportData = await this.getExpensifyReport(reportId);

      if (!reportData || !reportData.expenses) {
        throw new Error('No expenses found in report');
      }

      // Get project details
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('organization_id, project_code')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      const results: ExpenseSyncResult[] = [];

      // Process each expense
      for (const expensifyExpense of reportData.expenses) {
        try {
          const result = await this.createProjectExpenseFromExpensify(
            expensifyExpense,
            projectId,
            reportId
          );
          results.push(result);

          // Log successful import
          await this.logSyncActivity('import', 'expense', result.expense_id, 'success', {
            expensify_expense_id: expensifyExpense.expenseID,
            amount: expensifyExpense.amount,
          });
        } catch (error) {
          results.push({
            success: false,
            expensify_expense_id: expensifyExpense.expenseID,
            amount: expensifyExpense.amount,
            error: error.message,
          });

          // Log failed import
          await this.logSyncActivity('import', 'expense', null, 'error', {
            expensify_expense_id: expensifyExpense.expenseID,
            error_message: error.message,
          });
        }
      }

      return results;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get report details from Expensify
   */
  async getExpensifyReport(reportId: string): Promise<ExpensifyReport> {
    const requestData = {
      type: 'get',
      credentials: {
        partnerUserID: this.config?.partner_user_id,
        partnerUserSecret: this.config?.partner_user_secret,
      },
      inputSettings: {
        type: 'report',
        reportIDList: reportId,
      },
      outputSettings: {
        fileExtension: 'json',
      },
    };

    const response = await this.makeExpensifyRequest(requestData);
    return response;
  }

  /**
   * Create project expense from Expensify expense
   */
  private async createProjectExpenseFromExpensify(
    expensifyExpense: ExpensifyExpense,
    projectId: string,
    reportId: string
  ): Promise<ExpenseSyncResult> {
    // Map Expensify category to expense type
    const expenseType = this.mapExpensifyCategory(expensifyExpense.category);

    const { data: expense, error } = await supabase
      .from('project_expenses')
      .insert({
        project_id: projectId,
        description: `${expensifyExpense.merchant} - ${expensifyExpense.comment || ''}`,
        amount: expensifyExpense.amount,
        expense_type: expenseType,
        vendor: expensifyExpense.merchant,
        expense_date: expensifyExpense.created,
        expensify_report_id: reportId,
        external_id: expensifyExpense.expenseID,
        status: this.shouldAutoApprove(expensifyExpense.amount) ? 'approved' : 'pending',
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select('id')
      .single();

    if (error) throw error;

    return {
      success: true,
      expense_id: expense.id,
      expensify_expense_id: expensifyExpense.expenseID,
      amount: expensifyExpense.amount,
    };
  }

  /**
   * Set up webhook for real-time expense updates
   */
  async setupWebhook(organizationId: string, webhookUrl: string): Promise<void> {
    try {
      // In a real implementation, you would register the webhook with Expensify
      // For now, we'll store the webhook configuration
      await supabase.from('integration_configs').upsert({
        organization_id: organizationId,
        integration_type: 'expensify',
        config_data: {
          ...this.config,
          webhook_url: webhookUrl,
        },
        is_active: true,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process webhook payload from Expensify
   */
  async processWebhook(payload: any): Promise<void> {
    try {
      const { reportID, action, userEmail } = payload;

      switch (action) {
        case 'SUBMITTED':
          await this.handleReportSubmitted(reportID, userEmail);
          break;
        case 'APPROVED':
          await this.handleReportApproved(reportID);
          break;
        case 'EXPENSE_ADDED':
          await this.handleExpenseAdded(payload);
          break;
        default:
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle report submitted webhook
   */
  private async handleReportSubmitted(reportId: string, userEmail: string): Promise<void> {
    // Find project associated with the user
    const { data: projectMember, error } = await supabase
      .from('project_team_members')
      .select('project_id')
      .eq('email', userEmail)
      .eq('member_type', 'employee')
      .single();

    if (error || !projectMember) {
      return;
    }

    // Auto-import expenses if enabled
    if (this.config?.auto_import) {
      await this.importExpensesFromReport(reportId, projectMember.project_id);
    }
  }

  /**
   * Handle report approved webhook
   */
  private async handleReportApproved(reportId: string): Promise<void> {
    // Update all expenses from this report to approved status
    await supabase
      .from('project_expenses')
      .update({ status: 'approved' })
      .eq('expensify_report_id', reportId)
      .eq('status', 'pending');
  }

  /**
   * Handle expense added webhook
   */
  private async handleExpenseAdded(payload: any): Promise<void> {
    // This would handle individual expense additions
  }

  /**
   * Map Expensify expense category to internal expense type
   */
  private mapExpensifyCategory(category: string): string {
    const categoryMap: Record<string, string> = {
      Airfare: 'travel',
      'Ground Transportation': 'transportation',
      Lodging: 'accommodation',
      'Meals and Entertainment': 'meals',
      'Office Supplies': 'materials',
      Software: 'software',
      Internet: 'utilities',
      Phone: 'utilities',
      Taxi: 'transportation',
      Uber: 'transportation',
      Hotel: 'accommodation',
      Restaurant: 'meals',
    };

    return categoryMap[category] || 'other';
  }

  /**
   * Determine if expense should be auto-approved based on amount
   */
  private shouldAutoApprove(amount: number): boolean {
    return this.config?.auto_approve_threshold
      ? amount <= this.config.auto_approve_threshold
      : false;
  }

  /**
   * Sync expense status back to Expensify
   */
  async syncExpenseStatus(expenseId: string, status: string): Promise<void> {
    try {
      // Get expense details
      const { data: expense, error } = await supabase
        .from('project_expenses')
        .select('external_id, expensify_report_id')
        .eq('id', expenseId)
        .single();

      if (error || !expense.external_id) return;

      // Update expense in Expensify (placeholder - would use actual API)
      // Log the sync
      await this.logSyncActivity('export', 'expense', expenseId, 'success', {
        expensify_expense_id: expense.external_id,
        status: status,
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  }

  /**
   * Get expense reports for a date range
   */
  async getExpenseReports(startDate: string, endDate: string): Promise<ExpensifyReport[]> {
    const requestData = {
      type: 'get',
      credentials: {
        partnerUserID: this.config?.partner_user_id,
        partnerUserSecret: this.config?.partner_user_secret,
      },
      inputSettings: {
        type: 'report',
        filters: {
          startDate: startDate,
          endDate: endDate,
          reportState: 'SUBMITTED,APPROVED',
        },
      },
      outputSettings: {
        fileExtension: 'json',
      },
    };

    const response = await this.makeExpensifyRequest(requestData);
    return Array.isArray(response) ? response : [response];
  }

  /**
   * Log sync activity
   */
  private async logSyncActivity(
    syncType: 'import' | 'export',
    entityType: string,
    entityId: string | null,
    status: string,
    data: any
  ): Promise<void> {
    try {
      await supabase.from('integration_sync_log').insert({
        integration_type: 'expensify',
        sync_type: syncType,
        entity_type: entityType,
        entity_id: entityId,
        external_id: data.expensify_expense_id,
        status: status,
        error_message: data.error_message,
        request_data: data,
        response_data: data,
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  }

  /**
   * Test connection to Expensify
   */
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.config) {
        throw new Error('Service not initialized');
      }

      // Simple API test - get policy information
      const requestData = {
        type: 'get',
        credentials: {
          partnerUserID: this.config.partner_user_id,
          partnerUserSecret: this.config.partner_user_secret,
        },
        inputSettings: {
          type: 'policy',
        },
      };

      await this.makeExpensifyRequest(requestData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to connect to Expensify',
      };
    }
  }
}

export const expensifyService = new ExpensifyService();
export default expensifyService;
